import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, ClarityEvents } from '../utils/clarityTracking';
import { getModelsByBrandId } from '../services/modelService';
import { getServicesByModelId } from '../services/serviceService';

interface DealStyle {
  label: string;
  gradient: string;
  glowColor: string;
  tagColor: string;
  darkColor: string;
}

const SLOTS: DealStyle[] = [
  { label: 'iPhone 11', gradient: 'linear-gradient(120deg, #1a1000 0%, #3d2000 50%, #6b3500 100%)', glowColor: '#d97706', tagColor: '#fbbf24', darkColor: '#1a1000' },
  { label: 'iPhone 12', gradient: 'linear-gradient(120deg, #001510 0%, #003020 50%, #005030 100%)', glowColor: '#059669', tagColor: '#34d399', darkColor: '#001510' },
  { label: 'iPhone 13', gradient: 'linear-gradient(120deg, #001020 0%, #003050 50%, #005080 100%)', glowColor: '#0284c7', tagColor: '#38bdf8', darkColor: '#001020' },
  { label: 'iPhone 14', gradient: 'linear-gradient(120deg, #1a0010 0%, #500020 50%, #800030 100%)', glowColor: '#e11d48', tagColor: '#fb7185', darkColor: '#1a0010' },
  { label: 'iPhone 15', gradient: 'linear-gradient(120deg, #120020 0%, #2d0060 50%, #4a0090 100%)', glowColor: '#a855f7', tagColor: '#c084fc', darkColor: '#120020' },
];

interface CardData {
  modelId: number;
  modelImage: string;
  ourPrice: number;
  icarePrice: number;
}

const Skeleton: React.FC = () => (
  <div style={{
    height: 14, borderRadius: 6,
    background: 'rgba(255,255,255,0.12)',
    animation: 'shimmer 1.4s infinite',
  }} />
);

const IphoneBatteryTrendCarousel: React.FC = () => {
  // One entry per slot — null means still loading
  const [cardData, setCardData] = useState<(CardData | null)[]>(SLOTS.map(() => null));
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeftRef = useRef(0);
  const autoScrollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      try {
        const allModels = await getModelsByBrandId(1);

        // Resolve a modelId for each slot in parallel, then fetch its battery price
        await Promise.allSettled(
          SLOTS.map(async (slot, i) => {
            const model = allModels.find(m => m.name.toLowerCase().startsWith(slot.label.toLowerCase()));
            if (!model) return;

            const services = await getServicesByModelId(model.id);
            const battery = services.find(s => s.serviceName.toLowerCase().includes('battery'));
            if (!battery) return;

            setCardData(prev => {
              const next = [...prev];
              next[i] = {
                modelId: model.id,
                modelImage: model.image,
                ourPrice: battery.finalPrice ?? 0,
                icarePrice: battery.actualBrandPrice ?? 0,
              };
              return next;
            });
          })
        );
      } catch {
        // silently ignore
      }
    };
    load();
  }, []);

  useEffect(() => {
    autoScrollRef.current = setInterval(() => {
      setActiveIndex(prev => {
        const next = (prev + 1) % SLOTS.length;
        scrollToIndex(next);
        return next;
      });
    }, 5000);
    return () => { if (autoScrollRef.current) clearInterval(autoScrollRef.current); };
  }, []);

  const scrollToIndex = (index: number) => {
    scrollRef.current?.scrollTo({ left: index * (300 + 12), behavior: 'smooth' });
  };

  const resetAutoScroll = () => {
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
    autoScrollRef.current = setInterval(() => {
      setActiveIndex(prev => {
        const next = (prev + 1) % SLOTS.length;
        scrollToIndex(next);
        return next;
      });
    }, 5000);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
    scrollLeftRef.current = scrollRef.current?.scrollLeft ?? 0;
    if (autoScrollRef.current) clearInterval(autoScrollRef.current);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    scrollRef.current.scrollLeft = scrollLeftRef.current - (e.pageX - scrollRef.current.offsetLeft - startX.current);
  };

  const handleMouseUp = () => { isDragging.current = false; resetAutoScroll(); };

  const handleBook = (slot: DealStyle, data: CardData | null) => {
    trackEvent(ClarityEvents.IPHONE_BANNER_CLICK, { source: 'battery_trend', model: slot.label });
    if (data) {
      const slug = slot.label.toLowerCase().replace(/\s+/g, '-') + '-battery-replacement-price';
      navigate(`/services/${data.modelId}/${slug}`);
    } else {
      navigate('/brand/1');
    }
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <style>{`
        @keyframes shimmer {
          0%   { opacity: 0.4; }
          50%  { opacity: 0.8; }
          100% { opacity: 0.4; }
        }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ width: 4, height: 20, background: '#c0007a', borderRadius: 2 }} />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>Trend</h3>
        <span style={{
          fontSize: 10, fontWeight: 700,
          background: 'linear-gradient(90deg, #f97316, #ef4444)',
          color: '#fff', borderRadius: 20, padding: '2px 8px', letterSpacing: 0.4,
        }}>🔥 HOT</span>
      </div>

      {/* Carousel */}
      <div
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          display: 'flex', gap: 12, overflowX: 'auto',
          scrollSnapType: 'x mandatory', paddingBottom: 0,
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', msOverflowStyle: 'none', cursor: 'grab',
        }}
      >
        {SLOTS.map((slot, i) => {
          const data = cardData[i];
          const savings = data ? data.icarePrice - data.ourPrice : 0;
          const pct = data && data.icarePrice > 0 ? Math.round((savings / data.icarePrice) * 100) : 0;

          return (
            <div
              key={slot.label}
              onClick={() => handleBook(slot, data)}
              style={{
                minWidth: 300, height: 148, scrollSnapAlign: 'start',
                borderRadius: 16, background: slot.gradient,
                display: 'flex', flexDirection: 'row', overflow: 'hidden',
                flexShrink: 0, userSelect: 'none', cursor: 'pointer', position: 'relative',
              }}
            >
              {/* Glow */}
              <div style={{
                position: 'absolute', right: -20, top: '50%', transform: 'translateY(-50%)',
                width: 160, height: 160, borderRadius: '50%',
                background: slot.glowColor, opacity: 0.18, filter: 'blur(30px)', pointerEvents: 'none',
              }} />

              {/* Left: text */}
              <div style={{
                flex: 1, padding: '14px 0 14px 16px',
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between', zIndex: 1,
              }}>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 4 }}>
                    MINTO REPAIRS
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', lineHeight: 1.2, marginBottom: 2 }}>
                    {slot.label}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: slot.tagColor, marginBottom: 10 }}>
                    Battery Replacement
                  </div>
                </div>

                <div>
                  {data ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
                        <span style={{ fontSize: 24, fontWeight: 900, color: '#fff', lineHeight: 1 }}>
                          ₹{data.ourPrice.toLocaleString('en-IN')}
                        </span>
                        {data.icarePrice > 0 && (
                          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', textDecoration: 'line-through' }}>
                            ₹{data.icarePrice.toLocaleString('en-IN')}
                          </span>
                        )}
                      </div>
                      {pct > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, background: '#22c55e', color: '#fff', borderRadius: 4, padding: '2px 6px' }}>
                            {pct}% OFF
                          </span>
                          <span style={{ fontSize: 10, color: '#4ade80', fontWeight: 600 }}>
                            Save ₹{savings.toLocaleString('en-IN')}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 16 }}>
                      <div style={{ width: '60%' }}><Skeleton /></div>
                      <div style={{ width: '80%' }}><Skeleton /></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: model image or skeleton */}
              <div style={{
                width: 130, flexShrink: 0, alignSelf: 'stretch',
                position: 'relative', overflow: 'hidden',
              }}>
                {data ? (
                  <>
                    <img
                      src={data.modelImage}
                      alt={slot.label}
                      draggable={false}
                      style={{
                        position: 'absolute', inset: 0,
                        width: '100%', height: '100%',
                        objectFit: 'cover', objectPosition: 'center top',
                      }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                    <div style={{
                      position: 'absolute', inset: 0,
                      background: `linear-gradient(to right, ${slot.darkColor} 0%, ${slot.darkColor}CC 20%, transparent 55%)`,
                      zIndex: 2, pointerEvents: 'none',
                    }} />
                  </>
                ) : (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(255,255,255,0.06)',
                    animation: 'shimmer 1.4s infinite',
                  }} />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Dot indicators */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 5, marginTop: 10 }}>
        {SLOTS.map((_, i) => (
          <div
            key={i}
            onClick={() => { scrollToIndex(i); setActiveIndex(i); resetAutoScroll(); }}
            style={{
              width: i === activeIndex ? 18 : 6, height: 6, borderRadius: 3,
              background: i === activeIndex ? '#c0007a' : '#d1d5db',
              cursor: 'pointer', transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default IphoneBatteryTrendCarousel;
